import { useEffect, useState, useMemo, useCallback } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from '@mui/material';
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import ListViewSettings from "../../components/ListViewSettings";
import { getAllEquipments, deleteEquipments } from "../../services/EquipmentServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { buildEquipmentQueryParams } from "../../utils/listViewDateRanges";
import { printEquipment } from "../../utils/equipmentPrint";

function EquipmentTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const [allRecords, setAllRecords] = useState([]);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const [listViewConfig, setListViewConfig] = useState(null);

    const applyListFilters = useCallback((records, config) => {
        const statuses = config?.record_statuses;
        if (!statuses || !statuses.length) {
            return records;
        }
        const allowActive = statuses.includes('true');
        const allowInactive = statuses.includes('false');
        return records.filter((row) => {
            const isActive = String(row.status).toLowerCase() === 'active';
            if (isActive) return allowActive;
            return allowInactive;
        });
    }, []);

    const summaryTotals = useMemo(() => {
        const records = data.records || [];
        const active = records.filter((row) => String(row.status).toLowerCase() === 'active').length;
        const meterWorking = records.filter((row) => String(row.meter_status).toLowerCase() === 'working').length;
        const transitMixers = records.filter((row) =>
            String(row.equip_type || '').toUpperCase().includes('TRANSIT MIXER')
        ).length;
        const pumps = records.filter((row) =>
            String(row.equip_type || '').toUpperCase().includes('PUMP')
        ).length;
        const avgValues = records
            .map((row) => Number(row.avg_km_per_litre) || 0)
            .filter((value) => value > 0);
        const avgKmPerLitre = avgValues.length
            ? (avgValues.reduce((sum, value) => sum + value, 0) / avgValues.length)
            : 0;
        const totalKm = records.reduce((sum, row) => sum + (Number(row.km) || 0), 0);
        const totalHm = records.reduce((sum, row) => sum + (Number(row.hm) || 0), 0);
        const totalQty = records.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
        const totalTrips = records.reduce((sum, row) => sum + (Number(row.trips) || 0), 0);
        const totalDiesel = records.reduce((sum, row) => sum + (Number(row.diesel) || 0), 0);
        return {
            total: records.length,
            active,
            inactive: records.length - active,
            meterWorking,
            transitMixers,
            pumps,
            avgKmPerLitre,
            totalKm,
            totalHm,
            totalQty,
            totalTrips,
            totalDiesel,
        };
    }, [data.records]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Total',
            value: String(summaryTotals.total),
            gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #40B8E8 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
        {
            label: 'Active',
            value: String(summaryTotals.active),
            gradient: 'linear-gradient(135deg, #0E6A98 0%, #0090D8 55%, #40B8E8 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
        {
            label: 'KM',
            value: String(summaryTotals.totalKm),
            gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #5EC8F0 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.28)',
        },
        {
            label: 'HM',
            value: String(summaryTotals.totalHm),
            gradient: 'linear-gradient(135deg, #305870 0%, #186098 55%, #5AA8D8 100%)',
            shadow: '0 8px 20px rgba(48, 88, 112, 0.24)',
        },
        {
            label: 'Qty',
            value: Number(summaryTotals.totalQty || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #305870 0%, #186098 55%, #5AA8D8 100%)',
            shadow: '0 8px 20px rgba(24, 96, 152, 0.24)',
        },
        {
            label: 'Trips',
            value: String(summaryTotals.totalTrips),
            gradient: 'linear-gradient(135deg, #0E6A98 0%, #0090D8 55%, #5EC8F0 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
        {
            label: 'Diesel',
            value: Number(summaryTotals.totalDiesel || 0).toFixed(2),
            gradient: 'linear-gradient(135deg, #305870 0%, #4A6B80 55%, #7A9AAD 100%)',
            shadow: '0 8px 20px rgba(48, 88, 112, 0.2)',
        },
    ]), [summaryTotals]);

    const columns = useMemo(
        () => [
            {
                fieldName: 'equip_type', 
                headerName: 'Vehicle Type'
            },
           
            {
                fieldName: "equip_name",
                headerName: 'Vehicle No./SI.No.'
            },
            {
                fieldName: "capacity",
                headerName: 'Capacity'
            },
            {
                fieldName: "km",
                headerName: 'KM'
            },
            {
                fieldName: "hm",
                headerName: 'HM'
            },
            {
                fieldName: "qty",
                headerName: 'Qty'
            },
            {
                fieldName: "trips",
                headerName: 'Trips'
            },
            {
                fieldName: "diesel",
                headerName: 'Diesel'
            },
            {
                fieldName: "avg_km_per_litre",
                headerName: 'Avg KM/ Litre'
            },
            {
                fieldName: "vendor",
                headerName: 'Vendor'
            },
            {
                fieldName: "insurance_date",
                headerName: 'Insurance Due Date'
            },
            {
                fieldName: "permit_date",
                headerName: 'Permit Due Date'
            },
            {
                fieldName: "fc_date",
                headerName: 'FC Due Date'
            },
            {
                fieldName: "meter_status",
                headerName: 'Meter Status'
            },
            {
                fieldName: "user_remarks",
                headerName: 'Remarks'
            },
            {
                fieldName: "status",
                headerName: 'Status'
            },
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
        ],
        [],);

    const onEditEquipment = (equipmentId, event) => {
       navigate(`/EquipmentEdit/${equipmentId}/`);
    };

    const onPrintEquipment = (equipmentId, event) => {
        event.preventDefault();
        const listRow = data.records.find((row) => String(row.id) === String(equipmentId)) || null;
        setIsLoading(true);
        printEquipment(cookies, equipmentId, listRow)
            .then(() => setIsLoading(false))
            .catch((error) => {
                setIsLoading(false);
                displayError(error?.response?.data ?? error.message, 'Print Failed');
            });
    };

    const onDeleteEquipment = (equipmentId, event) => {
        event.preventDefault();
        Swal.fire({title: 'Are you sure to Delete?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 
            setIsLoading(true);
            deleteEquipments(cookies, equipmentId); 
            console.log(`Eqipment with id ${equipmentId} deleted`);
            Swal.fire('Deleted Successfully!', '', 'success');
            setRefreshKey(oldKey => oldKey +1)
          } else if (result.isDismissed) {    
            Swal.fire('Not Deleted', '', 'info')  
        }
        });
    };

    const handleSettingsSaved = (config) => {
        setListViewConfig(config || {});
        setRefreshKey((oldKey) => oldKey + 1);
        Swal.fire('Updated!', '', 'success');
    };

    const handleCSVExport = (event) => {
        console.log('CSV Export');
        exportAsCSV(
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(c => c.headerName),
            data.records.map(r => {

                const csvRow = {
                    equip_type: r.equip_type,
                    equip_name: r.equip_name,
                    capacity:r.capacity,
                    km: r.km,
                    hm: r.hm,
                    qty: r.qty,
                    trips: r.trips,
                    diesel: r.diesel,
                    avg_km_per_litre: r.avg_km_per_litre,
                    vendor:r.vendor,
                    insurance_date:r.insurance_date,
                    permit_date:r.permit_date,
                    fc_date:r.fc_date,
                    meter_status:r.meter_status,
                    user_remarks:r.user_remarks,
                    status:r.status,
                }
                return csvRow;
                }));
    };

    const handlePDFExport = (event) => {
        console.log('PDF Export');
        exportAsPDF(
            'List of Equipment Form',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(r => ({
                equip_type: r.equip_type,
                equip_name: r.equip_name,
                capacity:r.capacity,
                km: r.km,
                hm: r.hm,
                qty: r.qty,
                trips: r.trips,
                diesel: r.diesel,
                avg_km_per_litre: r.avg_km_per_litre,
                vendor:r.vendor,
                insurance_date:r.insurance_date,
                permit_date:r.permit_date,
                fc_date:r.fc_date,
                meter_status:r.meter_status,
                user_remarks:r.user_remarks,
                status:r.status,
            })),
            'Equipment_Forms.pdf'
        );
    };

    useEffect(() => {
        setIsLoading(true);
        getListViewSettings(cookies, 'equipment')
        .then((settingsResponse) => {
            const config = settingsResponse?.config || {};
            setListViewConfig(config);
            return getAllEquipments(cookies, buildEquipmentQueryParams(config))
                .then((equipmentsList) => ({ equipmentsList, config }));
        })
        .then(({ equipmentsList, config }) => {
            const tableData = (equipmentsList.equipment_list || [])
            .map(r => ({
                id :r.id,
                equip_type: r.equip_type.name,
                equip_name: r.equip_name,
                capacity:r.capacity,
                km: Number(r.km) || 0,
                hm: Number(r.hm) || 0,
                qty: Number(r.qty) || 0,
                trips: Number(r.trips) || 0,
                diesel: Number(r.diesel) || 0,
                avg_km_per_litre: Number(r.avg_km_per_litre) || 0,
                vendor:r.vendor.name,
                insurance_date:r.insurance_date,
                permit_date:r.permit_date,
                fc_date:r.fc_date,
                meter_status:r.meter_status ? "working":"not_working",
                user_remarks:r.user_remarks,
                status:r.status ? "active":"inactive" ,
                hide_row_edit: "No",
                hide_row_delete:r.system_field?"Yes":"No",
                hide_row_print: "No",
            }));
            setAllRecords(tableData);
            setData({
                total: tableData.length,
                records: applyListFilters(tableData, config)
            });
            setIsLoading(false);
        })
        .catch((error) => {
            setIsLoading(false);
            displayError(error?.response?.data ?? error.message, 'Failed to load vehicles / equipment');
        });
        }, [cookies, refreshKey, applyListFilters]);
    
    return (
        <div id="outer-container"  className="App" > 
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/Equipment"
                    addNewLabel="New Vehicle / Equipment"
                    homeLink="/Home"
                />
                <div className="container">
                    <PremiumPageHeader
                        module="FLEET"
                        title="List of Vehicles / Equipment"
                        subtitle="Configure filters in General Settings. The table loads based on your saved preferences."
                        modeLabel="VIEW MODE"
                        headerActions={
                            <ListViewSettings
                                viewKey="equipment"
                                onSaved={handleSettingsSaved}
                            />
                        }
                    />
                </div>

                <Box
                    className="container"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1.25,
                        flexWrap: 'wrap',
                        mb: 2.5,
                        py: 1,
                        overflowX: 'auto',
                    }}
                >
                    {summaryCards.map((card) => (
                        <Box
                            key={card.label}
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 128,
                                borderRadius: '12px',
                                background: card.gradient,
                                boxShadow: card.shadow,
                                px: 1.75,
                                py: 1.1,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9, lineHeight: 1.2 }}>
                                {card.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mt: 0.25 }}>
                                {card.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='List of Vehicles / Equipment'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditEquipment}
                            onRowDelete={onDeleteEquipment}
                            onRowPrint={onPrintEquipment}
                            hidePrint={false}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                        />
                </div>
                </div> 
            </div>
        </div>
    );
}

export default EquipmentTable;
